import { html } from 'lit';
import { DataTableConfig, ColumnDefinition, FilterDefinition, ActionDefinition } from '../../common/data-table/index.js';

interface Asset {
  id: string;
  asset_id?: string;
  name: string;
  parent_id?: string;
  parent_name?: string | null;
  status: string;
  company_id?: string;
  photo_url?: string | null;
  created_at?: number;
  updated_at?: number;
  location_id?: string;
  cost?: number;
  purchase_date?: number;
  warranty_date?: number;
  fields?: Array<{ name: string; type: string; value: string }>;
  model?: any;
}

interface LocationRow { 
  id: string; 
  altId?: string; 
  name: string; 
}

export function createAssetsTableConfig(
  assets: Asset[],
  locations: LocationRow[],
  locationIndex: Map<string, string>,
  handlers: {
    onRowClick: (asset: Asset) => void;
    onDelete: (asset: Asset) => void;
    onAddAsset: () => void;
    onFilterChange?: (filters: Record<string, any>) => void;
    onSort?: (column: string, direction: 'asc' | 'desc') => void;
  }
): DataTableConfig<Asset> {

  const getLocationName = (location_id?: string) =>
    location_id ? (locationIndex.get(String(location_id)) ?? String(location_id)) : '';

  const renderStatusBadge = (status: string) => {
    const s = (status || 'Active').toLowerCase();
    const pill = (variant: string, text: string) => html`<sl-badge variant=${variant} pill>${text}</sl-badge>`;
    if (s.includes('inactive') || s.includes('retired')) return pill('neutral', status);
    if (s.includes('fault') || s.includes('down')) return pill('danger', status);
    return pill('success', status);
  };

  const formatDate = (ts?: number): string => {
    if (!ts || Number.isNaN(ts)) return '—';
    try {
      const d = new Date(ts);
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const yyyy = d.getFullYear();
      return `${dd}/${mm}/${yyyy}`;
    } catch { 
      return '—'; 
    }
  };

  const columns: ColumnDefinition<Asset>[] = [
    {
      key: 'name',
      label: 'Name',
      type: 'custom',
      sortable: true,
      renderer: (value: string, asset: Asset) => html`
        <span class="primary">${value}</span>
      `
    },
    {
      key: 'status',
      label: 'Status',
      type: 'custom',
      sortable: true,
      className: 'status-cell',
      renderer: (value: string) => renderStatusBadge(value)
    },
    {
      key: 'location_id',
      label: 'Location',
      type: 'custom',
      sortable: true,
      className: 'location-cell',
      renderer: (value: string) => html`
        <sl-badge pill variant="neutral">${getLocationName(value) || '—'}</sl-badge>
      `
    },
    {
      key: 'parent_name',
      label: 'Parent Asset',
      type: 'custom',
      className: 'parent-cell',
      renderer: (value: string) => value 
        ? html`<sl-badge pill>${value}</sl-badge>` 
        : html`Root Asset`
    },
    {
      key: 'created_at',
      label: 'Created',
      type: 'date',
      sortable: true,
      className: 'created-col',
      formatter: formatDate
    }
  ];

  const filters: FilterDefinition[] = [
    {
      key: 'status',
      type: 'select',
      label: 'Status',
      placeholder: 'All status',
      options: [
        { value: 'Active', label: 'Active' },
        { value: 'Inactive', label: 'Inactive' }
      ]
    },
    {
      key: 'location_id',
      type: 'select',
      label: 'Location',
      placeholder: 'All locations',
      options: locations.map(l => ({ value: l.id, label: l.name }))
    }
  ];

  const rowActions: ActionDefinition<Asset>[] = [
    {
      key: 'delete',
      label: 'Delete',
      variant: 'danger',
      icon: 'trash',
      size: 'small',
      onClick: (asset: Asset, e: Event) => {
        e.stopPropagation();
        handlers.onDelete(asset);
      }
    }
  ];

  const headerActions: ActionDefinition[] = [
    {
      key: 'add',
      label: 'Add Asset',
      variant: 'primary',
      icon: 'plus',
      size: 'medium',
      onClick: () => handlers.onAddAsset()
    }
  ];

  return {
    columns,
    data: assets,
    filters,
    rowActions,
    headerActions,
    searchPlaceholder: 'Search by name, code, location, parent…',
    pagination: {
      enabled: true,
      dynamic: true
    },
    emptyState: {
      icon: 'box',
      message: 'No assets found. Try adjusting filters or click "Add Asset".'
    },
    clickableRows: true,
    defaultSort: {
      key: 'created_at',
      direction: 'desc'
    },
    defaultFilters: {
      status: 'Active' // Default to "Active" status to exclude inactive assets
    },
    onRowClick: handlers.onRowClick,
    onFilterChange: handlers.onFilterChange,
    onSort: handlers.onSort
  };
}
